import React from "react";
import styled, { keyframes } from "styled-components";
import { FaApple, FaTrophy, FaStar, FaArrowRight, FaCalendarAlt } from "react-icons/fa";
import bgImage from "../assets/images/home-bg.png";
import logoGymly from "../assets/images/logos/gymly.png";
import logoArauco from "../assets/images/logos/arauco.png";
import logoRetailTrust from "../assets/images/logos/retailtrust.png";
import logoBaliSolarium from "../assets/images/logos/balisolarium.png";
import logoFunky from "../assets/images/logos/funky.png";

/* ─── animations ─── */

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-6px); }
`;

/* ─── styled ─── */

const HeroContainer = styled.section`
  min-height: 100vh;
  background-image: url(${bgImage});
  background-size: cover;
  background-position: center;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  text-align: center;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(10, 12, 30, 0.75) 0%,
      rgba(10, 12, 30, 0.55) 40%,
      rgba(10, 12, 30, 0.85) 100%
    );
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  max-width: 960px;
  padding: 100px 40px 80px;
  z-index: 2;

  @media (max-width: 900px) {
    padding: 100px 20px 60px;
  }
`;

const EyebrowStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(0, 200, 255, 0.08);
  border: 1px solid rgba(0, 200, 255, 0.25);
  color: #00c8ff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  padding: 8px 22px;
  border-radius: 50px;
  animation: ${fadeInUp} 1s ease-out 0.2s both;
`;

const HeroTitle = styled.h1`
  font-size: 3.6em;
  margin-bottom: 24px;
  line-height: 1.12;
  font-weight: 800;
  letter-spacing: -1px;
  background: linear-gradient(135deg, #ffffff 0%, #00c8ff 50%, #005bea 100%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${fadeInUp} 1s ease-out 0.4s both, ${shimmer} 5s linear infinite;

  @media (max-width: 900px) {
    font-size: 2.1em;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2em;
  margin-bottom: 40px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.75;
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
  animation: ${fadeInUp} 1s ease-out 0.6s both;

  @media (max-width: 900px) {
    font-size: 1.05em;
    margin-bottom: 32px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;
  align-items: center;
  margin-bottom: 56px;
  animation: ${fadeInUp} 1s ease-out 0.8s both;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 18px;
    margin-bottom: 44px;
  }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #00c8ff 0%, #005bea 100%);
  color: white;
  border: none;
  padding: 18px 40px;
  font-weight: 700;
  font-size: 1em;
  letter-spacing: 0.5px;
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.3s ease;
  text-transform: uppercase;
  text-decoration: none;
  font-family: inherit;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0, 200, 255, 0.35);
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 18px 36px;
  font-weight: 700;
  font-size: 1em;
  letter-spacing: 0.5px;
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.3s ease;
  text-transform: uppercase;
  font-family: inherit;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.35);
    transform: translateY(-2px);
  }
`;

/* ── social proof badges ── */

const BadgesRow = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 48px;
  animation: ${fadeInUp} 1s ease-out 1s both;

  @media (max-width: 600px) {
    gap: 10px;
    margin-bottom: 36px;
  }
`;

const Badge = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "badgeColor",
})`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${({ badgeColor }) => badgeColor}12;
  border: 1px solid ${({ badgeColor }) => badgeColor}30;
  padding: 8px 18px;
  border-radius: 50px;
  animation: ${float} 4s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay || "0s"};

  svg {
    color: ${({ badgeColor }) => badgeColor};
    font-size: 14px;
  }

  span {
    font-size: 12px;
    font-weight: 700;
    color: ${({ badgeColor }) => badgeColor};
    letter-spacing: 0.3px;
    white-space: nowrap;
  }

  @media (max-width: 600px) {
    padding: 6px 14px;
    gap: 6px;
    svg { font-size: 12px; }
    span { font-size: 11px; }
  }
`;

const AppleRibbon = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.08) 0%,
    rgba(255, 255, 255, 0.02) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 12px 24px 12px 18px;
  border-radius: 50px;
  backdrop-filter: blur(12px);
  animation: ${fadeInUp} 1s ease-out 0.3s both;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);

  .apple-mark {
    color: #ffffff;
    font-size: 28px;
    line-height: 0;
  }

  .ribbon-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.1;
  }

  .ribbon-eyebrow {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.45);
  }

  .ribbon-title {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.3px;
    color: #ffffff;
    margin-top: 3px;
  }

  @media (max-width: 600px) {
    padding: 10px 18px 10px 14px;
    gap: 10px;
    .apple-mark { font-size: 22px; }
    .ribbon-eyebrow { font-size: 8px; }
    .ribbon-title { font-size: 11px; }
  }
`;

/* ── trusted by strip ── */

const TrustStrip = styled.div`
  animation: ${fadeInUp} 1s ease-out 1.2s both;
`;

const TrustLabel = styled.p`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.25);
  margin-bottom: 20px;
`;

const TrustLogos = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const TrustRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 28px;
  flex-wrap: wrap;

  & + & {
    margin-top: 18px;
  }

  @media (max-width: 600px) {
    gap: 16px;
    & + & {
      margin-top: 12px;
    }
  }
`;

const LogoLink = styled.a.withConfig({
  shouldForwardProp: (prop) => prop !== "highlight",
})`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  padding: 10px 16px;
  border-radius: 12px;
  background: ${({ highlight }) => (highlight ? "rgba(255,255,255,0.06)" : "transparent")};
  border: 1px solid ${({ highlight }) => (highlight ? "rgba(255,255,255,0.1)" : "transparent")};
  transition: all 0.3s ease;

  img {
    height: ${({ highlight }) => (highlight ? "36px" : "28px")};
    width: auto;
    object-fit: contain;
    filter: grayscale(100%) brightness(2);
    opacity: ${({ highlight }) => (highlight ? "0.85" : "0.55")};
    transition: all 0.3s ease;
  }

  span {
    font-size: ${({ highlight }) => (highlight ? "13px" : "11px")};
    font-weight: 700;
    color: ${({ highlight }) => (highlight ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.4)")};
    letter-spacing: 0.6px;
    text-transform: uppercase;
    transition: color 0.3s ease;
    white-space: nowrap;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.18);
    transform: translateY(-2px);

    img {
      filter: none;
      opacity: 1;
    }
    span {
      color: #fff;
    }
  }

  @media (max-width: 600px) {
    padding: 8px 12px;
    gap: 8px;
    img {
      height: ${({ highlight }) => (highlight ? "28px" : "22px")};
    }
    span {
      font-size: ${({ highlight }) => (highlight ? "11px" : "10px")};
    }
  }
`;

const WordmarkLink = styled.a.withConfig({
  shouldForwardProp: (prop) => prop !== "highlight",
})`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid transparent;
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.4);
  letter-spacing: 0.6px;
  text-transform: uppercase;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.18);
    color: #fff;
    transform: translateY(-2px);
  }
`;

/* ─── component ─── */

export const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <HeroContainer id="hero">
      <HeroContent>
        <EyebrowStack>
          <HeroTag>Software Studio</HeroTag>
          <AppleRibbon>
            <FaApple className="apple-mark" />
            <div className="ribbon-text">
              <span className="ribbon-eyebrow">Apple WWDC · Cupertino 2024</span>
              <span className="ribbon-title">
                Primer chileno en ganar el Swift Student Challenge
              </span>
            </div>
          </AppleRibbon>
        </EyebrowStack>
        <HeroTitle>
          Productos digitales con vara de clase mundial
        </HeroTitle>
        <HeroSubtitle>
          Boutique de software fundada por Martín Seguel, primer chileno premiado
          por Apple en el WWDC. Diseñamos y construimos productos para startups y
          empresas en Latinoamérica.
        </HeroSubtitle>

        <BadgesRow>
          <Badge badgeColor="#10b981" delay="0s">
            <FaApple />
            <span>Apple WWDC Winner</span>
          </Badge>
          <Badge badgeColor="#f59e0b" delay="0.5s">
            <FaTrophy />
            <span>100 Jóvenes Líderes</span>
          </Badge>
          <Badge badgeColor="#7c3aed" delay="1s">
            <FaStar />
            <span>Top 50 Developers</span>
          </Badge>
        </BadgesRow>

        <ButtonGroup>
          {/* CTA principal: agendar pesa más que el formulario, porque cierra en la misma llamada. */}
          <PrimaryButton as="a" href="/call">
            <FaCalendarAlt /> Agenda una llamada
          </PrimaryButton>
          <SecondaryButton onClick={() => scrollTo("contact")}>
            Escríbenos <FaArrowRight />
          </SecondaryButton>
        </ButtonGroup>

        <TrustStrip>
          <TrustLabel>Confían en nosotros</TrustLabel>
          <TrustLogos>
            <TrustRow>
              <LogoLink
                highlight
                href="https://gymly.cl"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={logoGymly} alt="Gymly" />
                <span>Gymly</span>
              </LogoLink>
              <LogoLink
                highlight
                href="https://arauco.com/chile/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={logoArauco} alt="Arauco" />
                <span>Arauco</span>
              </LogoLink>
              <LogoLink
                highlight
                href="https://retailtrust.cl"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={logoRetailTrust} alt="Retail Trust" />
                <span>Retail Trust</span>
              </LogoLink>
            </TrustRow>
            <TrustRow>
              <LogoLink href="#" onClick={(e) => e.preventDefault()}>
                <img src={logoBaliSolarium} alt="Bali Solarium" />
                <span>Bali Solarium</span>
              </LogoLink>
              <WordmarkLink href="#" onClick={(e) => e.preventDefault()}>
                Conecta Coaching
              </WordmarkLink>
              <LogoLink href="https://funky.app" target="_blank" rel="noopener noreferrer">
                <img src={logoFunky} alt="Funky" />
                <span>Funky</span>
              </LogoLink>
            </TrustRow>
          </TrustLogos>
        </TrustStrip>
      </HeroContent>
    </HeroContainer>
  );
};
